import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Check, X, HelpCircle, Loader2, AlertTriangle, Clock3, Image as ImageIcon, ArrowRight, ChevronDown, RotateCcw } from 'lucide-react';
import { Layout } from '../components/Layout';
import { api } from '../lib/api';
import { retryRecognition } from '../lib/retry';
import { formatDuration } from '../lib/format';

const PAGE_SIZE = 8;

type Item = {
  id: number;
  status: 'pending' | 'done' | 'failed' | 'rejected';
  rejected: number;
  summary_total: number | null;
  summary_correct: number | null;
  summary_wrong: number | null;
  summary_unknown: number | null;
  created_at: number;
  completed_at: number | null;
  first_expr: string | null;
  image_count: number | null;
};

function friendlyTime(ts: number): string {
  const d = new Date(ts * 1000);
  const now = new Date();
  const sameDay = d.toDateString() === now.toDateString();
  const yest = new Date(now);
  yest.setDate(now.getDate() - 1);
  const isYesterday = d.toDateString() === yest.toDateString();
  const hm = d.toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit', hour12: false });
  if (sameDay) return `今天 ${hm}`;
  if (isYesterday) return `昨天 ${hm}`;
  if (d.getFullYear() === now.getFullYear()) {
    return `${d.getMonth() + 1}月${d.getDate()}日 ${hm}`;
  }
  return `${d.getFullYear()}-${d.getMonth() + 1}-${d.getDate()} ${hm}`;
}

function StatCard({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-3">
      <div className="text-xs text-slate-500">{label}</div>
      <div className="mt-1 text-lg font-semibold text-slate-900">{value}</div>
      {sub && <div className="mt-0.5 text-xs text-slate-400">{sub}</div>}
    </div>
  );
}

function StatusBadge({ it }: { it: Item }) {
  if (it.rejected) {
    return (
      <span className="inline-flex items-center gap-1 rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-700">
        <AlertTriangle className="h-3 w-3" />
        非本题型
      </span>
    );
  }
  if (it.status === 'failed') {
    return (
      <span className="inline-flex items-center gap-1 rounded-full border border-red-200 bg-red-50 px-2 py-0.5 text-xs font-medium text-red-700">
        <X className="h-3 w-3" />
        识别失败
      </span>
    );
  }
  if (it.status === 'pending') {
    const stuck = Math.floor(Date.now() / 1000) - it.created_at > 5 * 60;
    return stuck ? (
      <span className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-xs font-medium text-slate-500">
        <Clock3 className="h-3 w-3" />
        超时未完成
      </span>
    ) : (
      <span className="inline-flex items-center gap-1 rounded-full border border-sky-200 bg-sky-50 px-2 py-0.5 text-xs font-medium text-sky-700">
        <Loader2 className="h-3 w-3 animate-spin" />
        识别中
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-700">
      <Check className="h-3 w-3" />
      已识别
    </span>
  );
}

type StatsBucket = { cnt: number; total: number; correct: number };
type Stats = { today: StatsBucket; week: StatsBucket };

export function HistoryPage() {
  const navigate = useNavigate();
  const [items, setItems] = useState<Item[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [retryingId, setRetryingId] = useState<number | null>(null);
  const [retryError, setRetryError] = useState<{ id: number; message: string } | null>(null);
  const [stats, setStats] = useState<Stats | null>(null);

  const loadStats = useCallback(async () => {
    const r = await api<Stats>('/api/v1/recognitions/stats');
    if (r.ok) setStats(r.data);
  }, []);

  const onRetry = async (id: number) => {
    if (retryingId) return;
    setRetryingId(id);
    setRetryError(null);
    const r = await retryRecognition(id);
    if (r.ok) {
      navigate(`/result/${r.newId}`);
    } else {
      setRetryingId(null);
      setRetryError({ id, message: r.message });
    }
  };

  const loadPage = useCallback(async (cursor?: number) => {
    const qs = new URLSearchParams({ limit: String(PAGE_SIZE) });
    if (cursor) qs.set('cursor', String(cursor));
    const r = await api<{ items: Item[] }>(`/api/v1/recognitions?${qs.toString()}`);
    if (!r.ok) return { items: [] as Item[], ended: true };
    const next = r.data.items;
    return { items: next, ended: next.length < PAGE_SIZE };
  }, []);

  useEffect(() => {
    (async () => {
      const [{ items: first, ended }] = await Promise.all([loadPage(), loadStats()]);
      setItems(first);
      setHasMore(!ended);
      setLoading(false);
    })();
  }, [loadPage, loadStats]);

  // pending 收尾时刷一下 stats
  useEffect(() => {
    const hasPending = items.some((it) => it.status === 'pending');
    if (hasPending) return;
    loadStats();
  }, [items.length, loadStats, items]);

  // 有 pending 时每 5 秒刷第一页并合并
  useEffect(() => {
    const hasPending = items.some((it) => it.status === 'pending');
    if (!hasPending) return;
    const timer = setInterval(async () => {
      const { items: fresh } = await loadPage();
      setItems((prev) => {
        const byId = new Map<number, Item>(prev.map((p) => [p.id, p]));
        for (const it of fresh) byId.set(it.id, it);
        return Array.from(byId.values()).sort((a, b) => b.created_at - a.created_at);
      });
    }, 5000);
    return () => clearInterval(timer);
  }, [items, loadPage]);

  const loadMore = async () => {
    if (loadingMore || !hasMore || items.length === 0) return;
    setLoadingMore(true);
    const last = items[items.length - 1];
    const { items: next, ended } = await loadPage(last.created_at);
    setItems((prev) => [...prev, ...next]);
    setHasMore(!ended);
    setLoadingMore(false);
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center gap-2 py-12 text-slate-400">
          <Loader2 className="h-5 w-5 animate-spin" />
          <span>加载中…</span>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="mb-5">
        <h1 className="text-2xl font-semibold text-slate-900">历史记录</h1>
        <p className="mt-1 text-sm text-slate-500">
          已加载 {items.length} 条{hasMore ? '' : '·已是全部'}
        </p>
      </div>

      {stats && (stats.today.cnt > 0 || stats.week.cnt > 0) && (
        <div className="mb-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
          <StatCard label="今日识别" value={`${stats.today.cnt} 次`} />
          <StatCard
            label="今日正确率"
            value={
              stats.today.total > 0
                ? `${Math.round((stats.today.correct / stats.today.total) * 100)}%`
                : '—'
            }
            sub={stats.today.total > 0 ? `共 ${stats.today.total} 题` : undefined}
          />
          <StatCard label="本周识别" value={`${stats.week.cnt} 次`} />
          <StatCard
            label="本周正确率"
            value={
              stats.week.total > 0
                ? `${Math.round((stats.week.correct / stats.week.total) * 100)}%`
                : '—'
            }
            sub={stats.week.total > 0 ? `共 ${stats.week.total} 题` : undefined}
          />
        </div>
      )}

      {items.length === 0 ? (
        <div className="rounded-xl border border-dashed border-slate-300 bg-white p-10 text-center">
          <ImageIcon className="mx-auto h-10 w-10 text-slate-300" />
          <p className="mt-3 text-sm text-slate-500">还没有识别记录</p>
          <Link
            to="/app"
            className="mt-4 inline-flex h-9 items-center gap-1 rounded-md bg-sky-600 px-3 text-sm font-medium text-white hover:bg-sky-700"
          >
            去识别一张
            <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      ) : (
        <ul className="space-y-2">
          {items.map((it) => {
            const total = it.summary_total ?? 0;
            const correct = it.summary_correct ?? 0;
            const wrong = it.summary_wrong ?? 0;
            const unknown = it.summary_unknown ?? 0;
            const rate = total > 0 ? Math.round((correct / total) * 100) : null;
            const interactive = it.status !== 'pending' || total > 0;
            const durationSec =
              it.completed_at && it.completed_at > it.created_at
                ? it.completed_at - it.created_at
                : null;
            const body = (
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-sm font-medium text-slate-800">
                    {friendlyTime(it.created_at)}
                  </span>
                  <StatusBadge it={it} />
                  {durationSec !== null && it.status !== 'pending' && (
                    <span className="text-xs text-slate-400">
                      · 耗时 {formatDuration(durationSec)}
                    </span>
                  )}
                </div>
                {it.status === 'done' && !it.rejected && (
                  <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
                    <span className="text-slate-500">
                      共 <span className="font-semibold text-slate-700">{total}</span> 题
                    </span>
                    <span className="inline-flex items-center gap-1 text-emerald-600">
                      <Check className="h-3.5 w-3.5" />
                      {correct}
                    </span>
                    <span className="inline-flex items-center gap-1 text-red-600">
                      <X className="h-3.5 w-3.5" />
                      {wrong}
                    </span>
                    <span className="inline-flex items-center gap-1 text-amber-600">
                      <HelpCircle className="h-3.5 w-3.5" />
                      {unknown}
                    </span>
                    {rate !== null && (
                      <span className="ml-auto text-xs text-slate-400">
                        正确率 {rate}%
                      </span>
                    )}
                  </div>
                )}
                {it.status === 'done' && !it.rejected && (it.first_expr || (it.image_count ?? 0) > 0) && (
                  <div className="mt-1 truncate text-xs text-slate-400">
                    {it.image_count && it.image_count > 1 ? `${it.image_count} 张 · ` : ''}
                    {it.first_expr ? `首题 ${it.first_expr}` : null}
                  </div>
                )}
                {it.status === 'failed' && (
                  <p className="mt-2 text-sm text-slate-500">没能识别出结果。</p>
                )}
                {it.rejected === 1 && (
                  <p className="mt-2 text-sm text-slate-500">图片不是珠心算题目。</p>
                )}
              </div>
            );
            const stuck = it.status === 'pending' && Math.floor(Date.now() / 1000) - it.created_at > 5 * 60;
            const canRetry = it.status === 'failed' || stuck;
            const isRetrying = retryingId === it.id;
            const cardClasses =
              'overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm transition-colors' +
              (interactive ? ' hover:border-sky-300' : ' opacity-70');

            return (
              <li key={it.id}>
                <div className={cardClasses}>
                  {interactive ? (
                    <Link
                      to={`/result/${it.id}`}
                      className="block p-3 transition-colors hover:bg-sky-50/30"
                    >
                      {body}
                    </Link>
                  ) : (
                    <div className="p-3">{body}</div>
                  )}
                  {canRetry && (
                    <div className="flex items-center justify-end gap-3 border-t border-slate-100 bg-slate-50/50 px-3 py-2">
                      {retryError && retryError.id === it.id && (
                        <span className="mr-auto text-xs text-red-600">{retryError.message}</span>
                      )}
                      <Link
                        to="/app"
                        className="inline-flex h-8 items-center gap-1 rounded-md px-2.5 text-xs font-medium text-slate-500 hover:text-slate-700"
                      >
                        重新上传
                      </Link>
                      <button
                        type="button"
                        onClick={() => onRetry(it.id)}
                        disabled={retryingId !== null}
                        className="inline-flex h-8 items-center gap-1 rounded-md bg-sky-600 px-3 text-xs font-medium text-white hover:bg-sky-700 disabled:cursor-not-allowed disabled:opacity-60"
                      >
                        {isRetrying ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RotateCcw className="h-3.5 w-3.5" />}
                        {isRetrying ? '正在启动…' : '重新识别'}
                      </button>
                    </div>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {items.length > 0 && hasMore && (
        <div className="mt-4 flex justify-center">
          <button
            type="button"
            onClick={loadMore}
            disabled={loadingMore}
            className="inline-flex h-9 items-center gap-1 rounded-md border border-slate-200 bg-white px-4 text-sm text-slate-600 hover:border-sky-300 hover:bg-sky-50 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {loadingMore ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <ChevronDown className="h-4 w-4" />
            )}
            {loadingMore ? '加载中…' : '加载更多'}
          </button>
        </div>
      )}
    </Layout>
  );
}
